import { CosmosEnums } from 'cosmos-components';
import { SciPagesEnum } from './components/shared/SciPage/types';
import { SciFlow, SciFlowPath } from './types/SciFlow';
/**
 * New Zealand locale is hardcoded until we agreed on a strategy
 * of selecting locale dynamically based on a several parameters.
 * Whether navigator.language and navigator.languages are important,
 * we should also consider adding Accept-Language header and
 * reading user settings coming from the back-end. There is also could be
 * a UI control to change locale/currency.
 */
export const DEFAULT_LOCAL = 'en';

export const languageOptions = [
    {
        id: 'en_US',
        label: 'EN',
        value: 'English',
    },
    {
        id: 'fr-ca',
        label: 'FR',
        value: 'French',
    },
];

export const acceptedlanguages = languageOptions.map((opt) =>
    opt.label?.toLowerCase(),
);

export const minimumPaymentAmount = 0.1;

export const excludeLocationCodesForRNZExternalAgent = [
    'chs',
    'zqs',
    'aks',
    'fox',
    'bch',
    'cam',
    'nsn',
    'dun',
    'ptn',
    'gre',
    'rot',
    'ivc',
    'wel',
    'hst',
    'eca',
];

export const excludeLocationCodesForRNZInternalAgent = ['chs', 'zqs', 'aks'];

export const ThlWebsiteUrl =
    'http://www.thlonline.com/Privacy/Pages/default.aspx';

export const ThlEmailUsUrl = 'mailto:reservationsusa@thlonline.com';
export const RoadBearEmailUrl = 'mailto:selfcheckin-help-usa@thlonline.com';
export const ElMonteEmailUrl = 'mailto:selfcheckin-help-usa@thlonline.com';
export const EuropeEmailUrl = 'mailto:enquiriesEU@thlonline.com';
export const CanaDreamEmailUrl = 'mailto:booking@canadream.com';

export const CountryName = {
    nz: 'New Zealand',
    au: 'Australia',
    cn: 'China',
    us: 'United States',
    ca: 'Canada',
    gb: 'Great Britain',
};

export const CountryCode = {
    nz: 'NZ',
    au: 'AU',
    cn: 'CN',
    us: 'US',
    ca: 'CA',
    gb: 'GB',
};

export const favoriteCountriesWithCodes = [
    { name: CountryName.nz, code: 'NZ' },
    { name: CountryName.au, code: 'AU' },
    { name: CountryName.cn, code: 'CN' },
    { name: CountryName.us, code: 'US' },
    { name: CountryName.gb, code: 'GB' },
    { name: CountryName.ca, code: 'CA' },
];

export const defaultMinAge = 21;

export const StateCountries = [CountryName.us, CountryName.au, CountryName.ca];

export const StateCountryCodes = [
    CountryCode.us,
    CountryCode.au,
    CountryCode.ca,
];

export const UNITEDSTATES = 0;

export const AUSTRALIA = 1;

export const CANADA = 2;

export const sciContentKeys = {
    sciCustomerDetailsPage: 'sci-customer-details-page',
    sciDriverDetailsPage: 'sci-driver-details-page',
    sciAdditionalDriverDetailsPage: 'sci-additional-driver-details-page',
    sciAccidentLiabilityPage: 'sci-accident-liability-page',
    sciExtrasPage: 'sci-extras-page',
    sciVideosPage: 'sci-videos-page',
    sciInfoBoxVideos: 'sci-info-box-videos',
    sciPaymentPage: 'sci-payments-page',
    termsAndConditionsAcceptance: 'terms-and-conditions-acceptance',
    sciSafetyVideosAcceptance: 'sci-safety-videos-acceptance',
    infoBoxCardAdminFee: 'info-box-card-admin-fee',
    sciBankTransferConfirmation: 'sci-bank-transfer-confirmation',
    sciOnlinePaymentConfirmation: 'sci-online-payment-confirmation',
    sciConfirmationPage: 'sci-confirmation-page',
    bankTransferInfo: 'bank-transfer-info',
    sciInfoBoxCustomerDetails: 'sci-info-box-customer-details',
    sciInfoBoxDriverDetails: 'sci-info-box-driver-details',
    sciInfoBoxAdditionalDriverDetails: 'sci-info-box-additional-driver-details',
    sciTimeslotsPage: 'sci-time-slots-page',
    sciInfoBoxTimeSlots: 'sci-info-box-time-slots',
    sciDocumentsPage: 'sci-documents-page',
    sciInfoBoxDocuments: 'sci-info-box-documents',
} as const;

export const sciVideoContentKeys = {
    sciVideosVideo: 'sci-videos-video',
} as const;

export const organizationIds = {
    [CosmosEnums.CountryCode.AU]: 'ff27eac8-a949-4d79-aa2d-87d9a4886eb5',
    [CosmosEnums.CountryCode.NZ]: '98eb24cc-ec7a-4f22-84a5-6b8209d3d166',
    [CosmosEnums.CountryCode.US]: '478242c4-3cbe-48af-9180-1e330e0b83d3',
    [CosmosEnums.CountryCode.UK]: '4e270349-56e5-4dea-b951-f9ef9ad8a1de',
    [CosmosEnums.CountryCode.CA]: 'b446b758-b8f6-4b65-afba-04dc1f08faa6',
};

export const currencySymbols = {
    [CosmosEnums.Currencies.AUD]: '$',
    [CosmosEnums.Currencies.NZD]: '$',
    [CosmosEnums.Currencies.USD]: '$',
    [CosmosEnums.Currencies.EUR]: '€',
    [CosmosEnums.Currencies.GBP]: '£',
    [CosmosEnums.Currencies.CAD]: '$',
};

export const Path = {
    [SciPagesEnum.UpdateCustomerPage]: '/updateCustomerInfo',
    [SciPagesEnum.AddDriverPage]: '/addDriver',
    [SciPagesEnum.AddAdditionalDriversPage]: '/addAdditionalDrivers',
    [SciPagesEnum.VehicleProtections]: '/vehicleProtections',
    [SciPagesEnum.Extras]: '/extras',
    [SciPagesEnum.TimeSlots]: '/timeSlots',
    [SciPagesEnum.Videos]: '/videos',
    [SciPagesEnum.Documents]: '/documents',
    [SciPagesEnum.Payment]: '/payment',
};

export const SCIDefaultFlowPaths: SciFlowPath[] = [
    {
        isCompleted: false,
        path: '/',
        pageEnum: SciPagesEnum.RetrieveSelfCheckInPage,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.UpdateCustomerPage],
        pageEnum: SciPagesEnum.UpdateCustomerPage,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.AddDriverPage],
        pageEnum: SciPagesEnum.AddDriverPage,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.AddAdditionalDriversPage],
        pageEnum: SciPagesEnum.AddAdditionalDriversPage,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.VehicleProtections],
        pageEnum: SciPagesEnum.VehicleProtections,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.Extras],
        pageEnum: SciPagesEnum.Extras,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.TimeSlots],
        pageEnum: SciPagesEnum.TimeSlots,
        condition: (args) => args.timeslotsAvailable,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.Videos],
        pageEnum: SciPagesEnum.Videos,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.Documents],
        pageEnum: SciPagesEnum.Documents,
        condition: (args) => args.documentsAvailable,
    },
    {
        isCompleted: false,
        path: Path[SciPagesEnum.Payment],
        pageEnum: SciPagesEnum.Payment,
    },
];

export interface AmazonConnectConfig {
    enabled: boolean;
    scriptUrl: string;
    widgetId: string;
    snippetId: string;
    loadInDevelopment?: boolean;
    styles?: {
        iconType?: string;
        openChat?: {
            color?: string;
            backgroundColor?: string;
        };
        closeChat?: {
            color?: string;
            backgroundColor?: string;
        };
    };
}

export const amazonConnectConfigs: Record<string, AmazonConnectConfig> = {
    'motek.sci': {
        enabled: true,
        scriptUrl: 'https://thl-production.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
        widgetId: '522c776d-8f16-4401-be66-078aee387b8b',
        snippetId: 'QVFJREFIZ0JXUDRpTEE4cmZyV2xXczFoK1FVRnFIUjZkUU9mYTlTR2FNYytKUWduQXdIb2hZY2E2SlpvYWMyN2tvanVJR2dMQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNb1gxOFA0aWlBY2hvSk1rSkFnRVFnQ3M5SWVMNUhaRVJNQkxuRFpaVUhpSnNGd29NalBxQTZHaGVxZzErNnloK0pjQVgvQ0l6dzdtWFpBSVc6Okt4SlFPc1M5djFTNFA5RUZVNW53YWJLNVBzeVByREhHdlE1czArMUtOS1VZSWswWlJIYnpTSVIxbDBpOWw1SWJKMTdkSndJTGdDR25vL0hzYVQ3OVdwdnQxSklueUYrS2phRGtadzJVdWw5RUJTQ0ZSZTZYRytQUXZtSTRQSGRzZEZkWGVhQ3pJaWJPemhQRUlLS0RVSFJMZEZ1ejdiUT0=',
        loadInDevelopment: true,
        styles: {
            iconType: 'CHAT',
            openChat: {
                color: '#ffffff',
                backgroundColor: '#CD2E0A',
            },
            closeChat: {
                color: '#ffffff',
                backgroundColor: '#CD2E0A',
            },
        },
    },
    'eu.sci': {
        enabled: false,
        scriptUrl: '',
        widgetId: 'amazon-connect-chat-widget-eu',
        snippetId: '',
        loadInDevelopment: false,
        styles: {
            iconType: 'CHAT',
            openChat: {
                color: '#ffffff',
                backgroundColor: '#B3232C',
            },
            closeChat: {
                color: '#ffffff',
                backgroundColor: '#B3232C',
            },
        },
    },
    'roadbearrv.sci': {
        enabled: false,
        scriptUrl: '',
        widgetId: 'amazon-connect-chat-widget-roadbear',
        snippetId: '',
        loadInDevelopment: false,
        styles: {
            iconType: 'CHAT',
            openChat: {
                color: '#ffffff',
                backgroundColor: '#B3232C',
            },
            closeChat: {
                color: '#ffffff',
                backgroundColor: '#B3232C',
            },
        },
    },
    'elmonterv.sci': {
        enabled: false,
        scriptUrl: '',
        widgetId: 'amazon-connect-chat-widget-elmonte',
        snippetId: '',
        loadInDevelopment: false,
        styles: {
            iconType: 'CHAT',
            openChat: {
                color: '#ffffff',
                backgroundColor: '#B3232C',
            },
            closeChat: {
                color: '#ffffff',
                backgroundColor: '#B3232C',
            },
        },
    },
    'canadream.sci': {
        enabled: false,
        scriptUrl:
            '',
        widgetId: 'amazon-connect-chat-widget-canadream',
        snippetId: '',
        loadInDevelopment: false,
        styles: {
            iconType: 'CHAT',
            openChat: {
                color: '#ffffff',
                backgroundColor: '#003366',
            },
            closeChat: {
                color: '#ffffff',
                backgroundColor: '#003366',
            },
        },
    },
};

export const SCISpecificFlow: SciFlow[] = [
    {
        condition: (args) =>
            [organizationIds[CosmosEnums.CountryCode.UK]].includes(args.orgId),
        paths: [
            {
                isCompleted: false,
                path: '/',
                pageEnum: SciPagesEnum.RetrieveSelfCheckInPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.UpdateCustomerPage],
                pageEnum: SciPagesEnum.UpdateCustomerPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.AddDriverPage],
                pageEnum: SciPagesEnum.AddDriverPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.AddAdditionalDriversPage],
                pageEnum: SciPagesEnum.AddAdditionalDriversPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.VehicleProtections],
                pageEnum: SciPagesEnum.VehicleProtections,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Extras],
                pageEnum: SciPagesEnum.Extras,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.TimeSlots],
                pageEnum: SciPagesEnum.TimeSlots,
                condition: (args) => args.timeslotsAvailable,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Videos],
                pageEnum: SciPagesEnum.Videos,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Documents],
                pageEnum: SciPagesEnum.Documents,
                condition: (args) => args.documentsAvailable,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Payment],
                pageEnum: SciPagesEnum.Payment,
            },
        ],
    },
    {
        condition: (args) =>
            [
                organizationIds[CosmosEnums.CountryCode.AU],
                organizationIds[CosmosEnums.CountryCode.NZ],
            ].includes(args.orgId),
        paths: [
            {
                isCompleted: false,
                path: '/',
                pageEnum: SciPagesEnum.RetrieveSelfCheckInPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.UpdateCustomerPage],
                pageEnum: SciPagesEnum.UpdateCustomerPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.AddDriverPage],
                pageEnum: SciPagesEnum.AddDriverPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.AddAdditionalDriversPage],
                pageEnum: SciPagesEnum.AddAdditionalDriversPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.VehicleProtections],
                pageEnum: SciPagesEnum.VehicleProtections,
                condition: (args) => !args.isRelocation,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Extras],
                pageEnum: SciPagesEnum.Extras,
                condition: (args) => !args.isRelocation,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.TimeSlots],
                pageEnum: SciPagesEnum.TimeSlots,
                condition: (args) => args.timeslotsAvailable,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Videos],
                pageEnum: SciPagesEnum.Videos,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Documents],
                pageEnum: SciPagesEnum.Documents,
                condition: (args) => args.documentsAvailable,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Payment],
                pageEnum: SciPagesEnum.Payment,
            },
        ],
    },
    {
        condition: (args) =>
            [organizationIds[CosmosEnums.CountryCode.US]].includes(args.orgId),
        paths: [
            {
                isCompleted: false,
                path: '/',
                pageEnum: SciPagesEnum.RetrieveSelfCheckInPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.UpdateCustomerPage],
                pageEnum: SciPagesEnum.UpdateCustomerPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.AddDriverPage],
                pageEnum: SciPagesEnum.AddDriverPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.AddAdditionalDriversPage],
                pageEnum: SciPagesEnum.AddAdditionalDriversPage,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.VehicleProtections],
                pageEnum: SciPagesEnum.VehicleProtections,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Extras],
                pageEnum: SciPagesEnum.Extras,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.TimeSlots],
                pageEnum: SciPagesEnum.TimeSlots,
                condition: (args) => args.timeslotsAvailable,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Videos],
                pageEnum: SciPagesEnum.Videos,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Documents],
                pageEnum: SciPagesEnum.Documents,
                condition: (args) => args.documentsAvailable,
            },
            {
                isCompleted: false,
                path: Path[SciPagesEnum.Payment],
                pageEnum: SciPagesEnum.Payment,
            },
        ],
    },
];
